import { useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { downloadAccountDocument } from "@/lib/brokerAccounts";
import { formatBondDate } from "@/lib/bondValueDisplay";
import { saveFile } from "@/lib/downloadFile";
import { messageFor } from "@/lib/errors";
import {
  formatInvoiceAmount,
  getInvoice,
  invoiceBondLabel,
  invoiceDocumentLabel,
  invoiceStatusLabel,
  invoiceStatusLines,
  readInvoiceStatus,
  type InvoiceDetail,
  type InvoiceDocument,
} from "@/lib/invoices";
import { InvoiceStatusChip } from "@/components/InvoiceStatusChip";
import { PageHeader } from "@/components/PageHeader";
import { buttonClass } from "@/components/buttonStyles";

// BRK-S187 — `/surety/invoices/:id`, the invoice detail (PMS-S507).
//
// READ-ONLY. The number, the account, the bonds with obligee and project, the
// amount, the dates, the broker-safe status and its lines, and the documents
// (cover letter, invoice, UCC warning letter) through the ACCOUNT's document
// route — the one download door for every broker-visible document (BRK-S146).
// No Pay button, no payment method or reference, no reason or note: none of it
// is published to a broker, and none of it has a home here.

export function InvoicePage() {
  const { id = "" } = useParams();
  const { authedRequest } = useAuth();
  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const fresh = await getInvoice(authedRequest, id);
        if (!cancelled) setInvoice(fresh);
      } catch (err) {
        if (!cancelled) setError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, id]);

  return (
    <>
      <PageHeader
        title={invoice ? `Invoice ${invoice.number}` : "Invoice"}
        meta={invoice?.account.name}
      />
      <section className="flex min-w-0 flex-col gap-6">
        {error && (
          <p role="alert" className="text-sm text-brk-danger">
            {error}
          </p>
        )}
        {!invoice && !error && (
          <p role="status" className="text-sm text-brk-dim">
            Loading invoice…
          </p>
        )}
        {invoice && <InvoiceBody invoice={invoice} />}
        {invoice ? (
          <Link
            to={`/surety/accounts/${encodeURIComponent(invoice.account.id)}`}
            className="text-sm text-brk-accent hover:underline"
          >
            ← Back to {invoice.account.name}
          </Link>
        ) : (
          <Link
            to="/surety/accounts"
            className="text-sm text-brk-accent hover:underline"
          >
            ← Back to accounts
          </Link>
        )}
      </section>
    </>
  );
}

function InvoiceBody({ invoice }: { invoice: InvoiceDetail }) {
  const status = readInvoiceStatus(invoice.status);
  const lines = invoiceStatusLines(invoice, new Date());
  return (
    <>
      <Panel id="invoice-status" title="Status">
        <div className="flex flex-wrap items-center gap-2">
          <InvoiceStatusChip status={status} />
        </div>
        <ul data-testid="invoice-status-lines" className="flex flex-col gap-1">
          {lines.map((line) => (
            <li key={line} className="text-sm text-brk-text">
              {line}
            </li>
          ))}
        </ul>
      </Panel>

      <Panel id="invoice-summary" title="Invoice">
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Fact label="Amount" value={formatInvoiceAmount(invoice.amount)} />
          <Fact label="Date issued" value={formatBondDate(invoice.issuedOn)} />
          <Fact label="Due date" value={formatBondDate(invoice.dueOn)} />
          <Fact label="Status" value={invoiceStatusLabel(status)} />
          <Fact label="Account" value={invoice.account.name}>
            <Link
              to={`/surety/accounts/${encodeURIComponent(invoice.account.id)}`}
              className="break-words text-brk-accent hover:underline"
            >
              {invoice.account.name}
            </Link>
          </Fact>
          {invoice.sbNumber && (
            <Fact label="Submission" value={invoice.sbNumber}>
              {invoice.bondSubmissionId ? (
                <Link
                  to={`/surety/submissions/${encodeURIComponent(invoice.bondSubmissionId)}`}
                  className="text-brk-accent hover:underline"
                >
                  {invoice.sbNumber}
                </Link>
              ) : (
                invoice.sbNumber
              )}
            </Fact>
          )}
        </dl>
      </Panel>

      <Panel id="invoice-bonds" title="Bonds">
        <ul className="flex flex-col gap-3">
          {invoice.bonds.map((bond, i) => (
            <li
              key={`${bond.kind}-${bond.bondNumber ?? i}`}
              className="flex min-w-0 flex-col gap-0.5 rounded-lg border border-brk-border p-3"
            >
              <span className="break-words text-sm font-medium text-brk-text">
                {invoiceBondLabel(bond)}
              </span>
              <span className="break-words text-sm text-brk-dim">
                {[bond.obligeeName, bond.projectName]
                  .filter(Boolean)
                  .join(" · ") || "—"}
              </span>
            </li>
          ))}
        </ul>
      </Panel>

      <Panel id="invoice-documents" title="Documents">
        <Downloads
          accountId={invoice.account.id}
          documents={invoice.documents}
        />
      </Panel>
    </>
  );
}

function Downloads({
  accountId,
  documents,
}: {
  accountId: string;
  documents: InvoiceDocument[];
}) {
  const { authedBlob } = useAuth();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (documents.length === 0) {
    return (
      <p className="text-sm text-brk-dim">
        The documents for this invoice aren’t ready yet.
      </p>
    );
  }

  async function download(doc: InvoiceDocument) {
    setBusy(doc.documentId);
    setError(null);
    try {
      saveFile(
        await downloadAccountDocument(authedBlob, accountId, doc.documentId),
        doc.filename,
      );
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col gap-2">
        {documents.map((doc) => (
          <li
            key={doc.documentId}
            className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-md border border-brk-border p-3 text-sm"
          >
            <span className="min-w-0 break-all text-brk-text">
              {invoiceDocumentLabel(doc.type)}
              <span className="text-brk-dim"> · {doc.filename}</span>
            </span>
            <button
              type="button"
              onClick={() => void download(doc)}
              disabled={busy !== null}
              aria-label={`Download ${invoiceDocumentLabel(doc.type)}`}
              className={`${buttonClass({ variant: "secondary", size: "sm" })} disabled:cursor-not-allowed disabled:opacity-60`}
            >
              {busy === doc.documentId ? "Preparing…" : "Download"}
            </button>
          </li>
        ))}
      </ul>
      {error && (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}
    </div>
  );
}

function Fact({
  label,
  value,
  children,
}: {
  label: string;
  value: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-brk-dim">{label}</dt>
      <dd className="break-words text-sm font-medium text-brk-text">
        {children ?? value}
      </dd>
    </div>
  );
}

function Panel({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section
      aria-labelledby={id}
      className="flex min-w-0 flex-col gap-4 rounded-xl border border-brk-border bg-brk-panel p-4 sm:p-6"
    >
      <h2 id={id} className="text-heading text-brk-text">
        {title}
      </h2>
      {children}
    </section>
  );
}
